import React, { useRef} from 'react';
import Navbar from '../components/navbar/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../components/CustomHook';
import pic2 from '../images/schritte/pic2.jpg';
import pic3 from '../images/schritte/pic3.jpeg';
import pic4 from '../images/schritte/pic4.jpg';
import EnergyhouseWidget from '../components/EnergyhouseWidget.js';
import { HashLink } from 'react-router-hash-link';

const SolarSteps = () => {
  // SEO-Optimierung für die Solaranlage Schritt-für-Schritt Seite
  useSEO(
    "Solaranlage Schritt für Schritt - Schmid Energieberatung",
    "Erfahren Sie in 5 einfachen Schritten, wie Sie zu Ihrer eigenen Solaranlage von Schmid Energieberatung kommen. Vom ersten Kontakt bis zur Inbetriebnahme.",
    "Solaranlage, Schritte, Anleitung, Photovoltaik, Energieberatung, Schmid, Solaranlage installieren, Solaranlage planen",
    "/logo512.png"
  );

  // Create refs for each step
  const stepRefs = useRef([]);

  // Function to scroll to a specific step
  const scrollToStep = (index) => {
    if (stepRefs.current[index]) {
      stepRefs.current[index].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const steps = [
    {
      number: "1",
      title: "Fragen beantworten",
      description: (
        <>
          Damit wir für Sie das bestmögliche Energiesystem planen können, benötigen wir nur ein paar kurze Angaben. In unserem Energiehaus können Sie bequem alle wichtigen Daten eintragen – etwa zur Dachform, zur Heizart oder zur Größe Ihres Haushalts.
          <br />
          <br />
          So haben unsere Energieexperten die ideale Grundlage, um Sie anschließend persönlich, unverbindlich und optimal zu beraten.
        </>
      ),
      image: null,
      alt: "Checkliste für Solaranlage"
    },
    {
      number: "2",
      title: "Kostenlose Vor-Ort-Termin",
      description: (
        <>
        <strong>Termin vereinbaren – Dach prüfen – persönlich beraten</strong>
        <br />
        <br />
        Nach Ihrer Anfrage besucht Sie einer unserer Fachberater direkt vor Ort. Dabei verschaffen
        wir uns ein genaues Bild von Ihrem Dach, prüfen alle relevanten Gegebenheiten und
        beantworten ausführlich Ihre Fragen. So stellen wir sicher, dass Ihre zukünftige Anlage
        perfekt zu Ihnen passt.
        <br />
        <br />
        Und das Beste: Dieser Service ist für Sie selbstverständlich <strong>kostenlos und unverbindlich</strong>.
        </>
      ),
      image: pic2,
      alt: "Beratung vor Ort"
    },
    {
      number: "3",
      title: "Individuelle Planung & Angebot",
      description: (
        <>
        • <strong>Individuelle Planung Ihrer Anlage</strong><br />
        • <strong>Unverbindliches, transparentes Angebot</strong><br />
        • <strong>Optional mit passender Finanzierung</strong>
        <br />
        <br />
        Auf Grundlage Ihrer persönlichen Daten – wie Stromverbrauch, Dachgröße, Form und
        Ausrichtung – entwickeln unsere Energieexperten von Schmid-Energieberatung GmbH
        eine maßgeschneiderte Lösung für Ihr Zuhause.
        <br />
        <br />
        Sie erhalten ein detailliertes Angebot, das exakt zu Ihren Bedürfnissen passt. Auf Wunsch
        integrieren wir auch eine Finanzierungslösung, damit Ihr Schritt in die Energiezukunft noch
        einfacher wird.
        <br />
        <br />
        Nehmen Sie sich die Zeit, das Angebot in Ruhe zu prüfen – und entscheiden Sie selbst, wann
        Sie mit uns den Weg in Ihre nachhaltige Unabhängigkeit starten möchten.
        </>
      ),
      image: pic3,
      alt: "Planung der Photovoltaikanlage"
    },
    {
      number: "4",
      title: "Installation & Anmeldung",
      description: (
        <>
        Nachdem Sie sich für unser Angebot entschieden haben, übernehmen wir für Sie alle
        notwendigen Schritte – unkompliziert, transparent und professionell:
        <br />
        <br />
        • <strong>Komponentenpaket</strong> – wir stellen alle technischen Bauteile vollständig für Sie zusammen.<br />
        • <strong>Organisation & Zeitplan</strong> – Transport und Installation werden von uns exakt koordiniert.<br />
        • <strong>Montage vom Meisterbetrieb</strong> – unsere Installateure installieren Ihre Anlage sicher und fachgerecht.<br />
        • <strong>Behördliche Formalitäten</strong> – wir kümmern uns um die Anmeldung bei Netzbetreiber und Bundesnetzagentur.<br />
        <br />
        Damit bleibt für Sie nur noch eins: sich auf Ihre neue Solaranlage zu freuen.
        </>
      ),
      image: pic4,
      alt: "Installation der Solaranlage"
    },
    {
      number: "5",
      title: "Inbetriebnahme",
      description: (
        <>
        Der große Moment ist gekommen: Nach Abschluss aller Arbeiten wird Ihre Anlage final
        geprüft und fachgerecht ans Hausnetz angeschlossen. Unser zertifizierter Elektriker nimmt die
        gesamte Technik offiziell ab – vom PV-System über den Stromspeicher bis hin zur
        intelligenten Steuerung.
        <br />
        <br />
        Damit ist Ihre neue Energiezentrale bereit für den Start.<br />
        <strong>Ab sofort erzeugen Sie Ihren eigenen Solarstrom – sicher, effizient und unabhängig.</strong>
        </>
      ),
      image: null,
      alt: "Inbetriebnahme der Solaranlage"
    }
  ];

  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary to-primary-light">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            In 5 einfachen Schritten zur Solaranlage
          </h1>
          <p className="text-2xl text-white max-w-3xl mx-auto">
            Sie möchten Ihre eigene Solaranlage besitzen? Wir machen es Ihnen so einfach wie nie.
            Mit nur 5 Schritten, vom ersten Kontakt bis zur stromerzeugenden Solaranlage auf Ihrem Dach!
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center cursor-pointer transition-transform hover:scale-105"
                onClick={() => scrollToStep(index)}
              >
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-white font-bold text-2xl mb-4 shadow-lg hover:bg-primary-dark">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{step.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Steps */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <div
              key={index}
              ref={(el) => (stepRefs.current[index] = el)}
              className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-12 mb-20 last:mb-0`}
            >
              {/* Conditionally render image container only if step has an image */}
              {step.image ? (
                <div className="md:w-1/2">
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <img
                      src={step.image}
                      alt={step.alt}
                      className="w-full h-80 object-cover"
                    />
                  </div>
                </div>
              ) : null}

              {/* Adjust width based on whether there's an image */}
              <div className={step.image ? "md:w-1/2" : "md:w-full"}>
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-white font-bold text-xl mr-6">
                    {step.number}
                  </div>
                  <h2 className="text-3xl font-bold text-primary">{step.title}</h2>
                </div>
                <p className="text-xl text-gray-700 mb-6">
                  {step.description}
                </p>
                {/* Add "Beratung vereinbaren" button only for step 2, using the same styling as Navbar */}
                {step.number === "2" && (
                  <HashLink
                    to="/kontakt"
                    smooth
                    className="bg-primary text-white rounded-lg font-semibold text-sm px-4 py-2 lg:px-6 lg:py-3 transition-all duration-200 hover:scale-105 inline-flex items-center justify-center whitespace-nowrap"
                  >
                    Beratung vereinbaren
                  </HashLink>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <EnergyhouseWidget />

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-primary to-primary-light rounded-2xl p-16 text-white">
          <h2 className="text-4xl font-bold mb-6">Jetzt den ersten Schritt machen</h2>
          <p className="text-2xl mb-10">
            Beantworten Sie uns vier schnelle Fragen rund um Ihre zukünftige Solaranlage
            und starten in Ihre Energiezukunft.
          </p>
          <div className="flex justify-center">
            <a
              href="/kontakt"
              className="bg-white text-primary rounded-lg font-semibold text-base px-8 py-4 lg:px-10 lg:py-5 transition-all duration-200 hover:scale-105"
            >
              Beratung vereinbaren
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SolarSteps;